<template>
  <v-col :md="colsMd" :lg="colsLg">
    <RaLoading
      :is-loading="isLoading"
    />
    <div class="content__box">
      <h1 class="content__box-title">
        Choose Your Reattendance Plan
      </h1>
      <p>Choose the Reattendance plan that suits you and your events best, from Replay where you are not running the events live to Small, Plus and Event Pro for small to medium sized events, and then Business and Platinum for larger scale events. If you need a plan larger than Platinum, please get in touch with our Sales team.</p>
      <v-row>
        <v-col md="6">
            <RaCurrencySelection
              v-model="isLoadingCurrencies"
              :user-currency-id="userCurrencyId"
              :is-read-only="hasStripeId"
              :default-currency-code="defaultCurrencyCode"
              @currency-selected="onSelectCurrency"
            />
        </v-col>
      </v-row>
    </div>

    <v-row v-if="canDisplayTable">
      <v-col cols="12">
        <div class="content__box content__box-spacing">
          <div class="pricing d-none d-md-block">
            <table class="table table-bordered">
              <thead class="pricing-sticy-head">
                <tr>
                  <!-- Table headers -->
                  <th
                    v-for="(column,index) in tableColumns"
                    :key="column.name"
                    scope="col"
                    :class="{'text-start' : index===0}"
                  >
                    <div class="pricing__thead-title">{{ column.title }}
                      <v-icon class="pricing__thead-info" v-if="showHelpIcon(column.title)" @click="openHelpInfo('id_' + index)" @focusout="closeHelpInfo('id_' + index)" tabindex="0" >
                        mdi-information-outline
                      </v-icon>
                    </div>
                    <div :ref="'id_' + index" :id="'id_' + index" class="pricing__info-popup" style="display:none">
                      <v-list class="event-tooltip">
                        <div class="pricing__info-body">
                          <div class="d-flex align-center justify-space-between">
                            <h2 class="pricing__info-title">
                              <v-icon class="help__info-title--icon">
                                mdi-help-circle-outline
                              </v-icon>
                              {{ getHelpColName(column.planName) }}
                            </h2>
                            <ra-modal-close-button @close-modal="closeHelpInfo ('id_' + index)" />
                          </div>
                          <div class="pricing__info-block">
                            <span class="pricing__info-desc" v-html="helpInfoColumn(column.planName)">{{ helpInfoColumn(column.planName) }}</span>
                          </div>
                        </div>
                      </v-list>
                    </div>
                    <v-btn
                      v-if="index!==0"
                      depressed
                      :disabled="buttonDetails(column.planName).isDisabled"
                      class="btn-primary mb-2 mt-2"
                      :class="{ 'btn-success' : buttonDetails(column.planName).isSubscribed }"
                      @click="buttonLink(column)"
                    >
                      {{ buttonDetails(column.planName).text }}
                    </v-btn>
                  </th>
                </tr>
              </thead>
              <tbody>
                <!-- Table rows -->
                <tr v-for="(row,index) in tableRows" :key="row.key" :class="row.isSectionHead? 'parent': 'child'">
                  <!-- row header -->
                  <th v-if="row.isSectionHead"
                      :id="row.id"
                      :colspan="columnCount"
                      scope="row"
                      class="pricing__section-head text-start btn__click"
                  >
                    <div class="pricing-group-head">
                      {{ row.name }}
                      <v-icon class="header-th">
                        mdi-arrow-down-circle
                      </v-icon>
                    </div>
                  </th>
                  <th v-if="!row.isSectionHead"
                      scope="row"
                      class="text-start"
                      :class="row.class"
                  >
                    <div class="pricing__head-title">
                      {{ row.name }}
                      <v-icon class="pricing__info" @click="openHelpInfo ('rowid_' + index)" @focusout="closeHelpInfo('rowid_' + index)" tabindex="0">
                        mdi-information-outline
                      </v-icon>
                    </div>
                  </th>
                  <!-- row entries -->
                  <template v-if="!row.isSectionHead">
                    <td
                      v-for="(column) in tableColumnsPlans"
                      :key="column.planName"
                      class="tagline"
                    >
                      <v-icon v-if="row[column.planName]===true" class="pricing__yes">
                        mdi-check
                      </v-icon>
                      <span v-else-if="row[column.planName]===false" class="">
                        -
                      </span>
                      <span v-else>{{ row[column.planName] }}</span>
                    </td>
                  </template>
                  <div :ref="'rowid_' + index" :id="'rowid_' + index" class="pricing__info-popup" style="display:none">
                    <v-list class="event-tooltip">
                      <div class="pricing__info-body">
                        <div class="d-flex align-center justify-space-between">
                          <h2 class="pricing__info-title">
                            <v-icon class="help__info-title--icon">
                              mdi-help-circle-outline
                            </v-icon>
                            {{ getHelpRowName(row.key) }}
                          </h2>
                          <ra-modal-close-button @close-modal="closeHelpInfo ('rowid_' + index)" />
                        </div>
                        <div class="pricing__info-block">
                          <span class="pricing__info-desc" v-html="helpInfoRow(row.key)">{{ helpInfoRow(row.key) }}</span>
                        </div>
                      </div>
                    </v-list>
                  </div>
                </tr>
              </tbody>
            </table>
            <!-- Table over -->
          </div>
          <div class="d-md-none">
            <!-- Replay -->
            <div class="pricing__mobile-block" v-for="(plan, index) in plansForMobileView" :key="plan.name">
              <div class="d-flex align-center justify-space-between">
                <h3 class="pricing__mobile-title">{{ plan.title }}
                  <v-icon class="pricing__info-mobile" v-if="showHelpIcon(plan.title)" @click="openHelpInfo('m_id_' + index)" @focusout="closeHelpInfo('m_id_' + index)">
                      mdi-information-outline
                  </v-icon>
                  <div :ref="'m_id_' + index" :id="'m_id_' + index" class="pricing__info-popup" style="display:none">
                    <v-list class="event-tooltip">
                      <div class="pricing__info-body">
                        <div class="d-flex align-center justify-space-between">
                          <h2 class="pricing__info-title">
                            <v-icon class="help__info-title--icon">
                              mdi-help-circle-outline
                            </v-icon>
                            {{ getHelpColName(plan.planName) }}
                          </h2>
                          <ra-modal-close-button @close-modal="closeHelpInfo ('m_id_' + index)" />
                        </div>
                        <div class="pricing__info-block">
                          <span class="pricing__info-desc" v-html="helpInfoColumn(plan.planName)">{{ helpInfoColumn(plan.planName) }}</span>
                        </div>
                      </div>
                    </v-list>
                  </div>
                </h3>
                <v-btn
                  outlined
                  :disabled="buttonDetails(plan.planName).isDisabled"
                  class="btn-primary mb-2 mt-2"
                  :class="{ 'btn-success' : buttonDetails(plan.planName).isSubscribed }"
                  @click="buttonLink(plan)"
                >
                  {{ buttonDetails(plan.planName).text }}
                </v-btn>
              </div>
              <div class="d-flex align-center justify-space-between pricing__mobile-price">
                <p class="pricing__mobile-subtitle">Price (Monthly)
                  <v-icon class="pricing__info-mobile"  @click="openHelpInfo ('m_month_price' + index)" @focusout="closeHelpInfo('m_month_price' + index)">
                        mdi-information-outline
                  </v-icon>
                </p>
                <div :ref="'m_month_price' + index" :id="'m_month_price' + index" class="pricing__info-popup" style="display:none">
                  <v-list class="event-tooltip">
                    <div class="pricing__info-body">
                      <div class="d-flex align-center justify-space-between">
                        <h2 class="pricing__info-title">
                          <v-icon class="help__info-title--icon">
                            mdi-help-circle-outline
                          </v-icon>
                          {{ getHelpRowName('priceMonthly') }}
                        </h2>
                        <ra-modal-close-button @close-modal="closeHelpInfo ('m_month_price' + index)" />
                      </div>
                      <div class="pricing__info-block">
                        <span class="pricing__info-desc" v-html="helpInfoRow('priceMonthly')">{{ helpInfoRow('priceMonthly') }}</span>
                      </div>
                    </div>
                  </v-list>
                </div>
                <p class="pricing__mobile-subtitle">Price (Annually)
                  <v-icon class="pricing__info-mobile" @click="openHelpInfo ('m_yearly_price' + index)" @focusout="closeHelpInfo('m_yearly_price' + index)" tabindex="0">
                        mdi-information-outline
                  </v-icon>
                </p>
                <div :ref="'m_yearly_price' + index" :id="'m_yearly_price' + index" class="pricing__info-popup" style="display:none">
                  <v-list class="event-tooltip">
                    <div class="pricing__info-body">
                      <div class="d-flex align-center justify-space-between">
                        <h2 class="pricing__info-title">
                          <v-icon class="help__info-title--icon">
                            mdi-help-circle-outline
                          </v-icon>
                          {{ getHelpRowName('priceAnnually') }}
                        </h2>
                        <ra-modal-close-button @close-modal="closeHelpInfo ('m_yearly_price' + index)" />
                      </div>
                      <div class="pricing__info-block">
                        <span class="pricing__info-desc" v-html="helpInfoRow('priceAnnually')">{{ helpInfoRow('priceAnnually') }}</span>
                      </div>
                    </div>
                  </v-list>
                </div>
              </div>
              <div class="d-flex align-center justify-space-between">
                <p class="pricing__mobile-price--data">{{ plan.monthly_price }}</p>
                <p class="pricing__mobile-price--data">{{ plan.yearly_price }}</p>
              </div>
              <!-- Accordion -->
              <v-expansion-panels accordion>
                <v-expansion-panel v-for="(section, sectionIndex) in plan.sections" :key="section.key + plan.planName">
                  <v-expansion-panel-header class="pricing__mobile-title">{{ section.name }}</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="d-flex align-center justify-space-between" v-for="(attribute, attributeIndex) in section.attributes">
                      <p class="pricing__accordion-title">{{ attribute.name }}
                        <v-icon class="pricing__info-mobile" @click="openHelpInfo ('m_attribute_'+ plan.planName + attribute.key + attributeIndex)" @focusout="closeHelpInfo('m_attribute_'+ plan.planName + attribute.key + attributeIndex)">
                        mdi-information-outline
                        </v-icon>
                      </p>
                      <div :ref="'m_attribute_'+ plan.planName + attribute.key + attributeIndex" :id="'m_attribute_'+ plan.planName + attribute.key + attributeIndex" class="pricing__info-popup" style="display:none">
                        <v-list class="event-tooltip">
                          <div class="pricing__info-body">
                            <div class="d-flex align-center justify-space-between">
                              <h2 class="pricing__info-title">
                                <v-icon class="help__info-title--icon">
                                  mdi-help-circle-outline
                                </v-icon>
                                {{ getHelpRowName(attribute.key) }}
                              </h2>
                              <ra-modal-close-button @close-modal="closeHelpInfo ('m_attribute_'+ plan.planName + attribute.key + attributeIndex)" />
                            </div>
                            <div class="pricing__info-block">
                              <span class="pricing__info-desc" v-html="helpInfoRow(attribute.key)">{{ helpInfoRow(attribute.key) }}</span>
                            </div>
                          </div>
                        </v-list>
                      </div>
                      <p class="pricing__accordion-data">
                        <v-icon v-if="attribute[plan.planName]===true" class="pricing__yes">
                          mdi-check
                        </v-icon>
                        <span v-else-if="attribute[plan.planName]===false" class="">
                        -
                      </span>
                        <span v-else>{{ attribute[plan.planName] }}</span>
                      </p>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Diloag to display exceeded plan limit data -->
    <v-dialog
      v-if="hasExceededData"
      v-model="hasExceededData"
      max-width="900"
    >
      <v-card>
        <div class="ra__form-heading">
          <v-card-title class="ra__modal-title">
            <h3 class="ra__modal-title--heading"> {{ $t('pricingTable.exceededTitle') }}  </h3>
          </v-card-title>
          <v-btn icon @click="hasExceededData = false" aria-label="Close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text>
          <v-row class="mt-5">
            <v-col cols="12" md="12">
              <p>  {{ $t('pricingTable.exceededBody') }} </p>
            </v-col>
            <div v-if="eventsData" class="ra__downgrade-popup col-12">
              <div v-for="event in eventsData" class="ra__downgrade-box">
                <h3 class="publicmodal__card-data--dark">{{ $t('pricingTable.event') }}  {{ event.event_name }}</h3>
                <p v-if="event.event_groups"> 
                  {{ $t('pricingTable.event_groups') }} {{ event.event_groups }} 
                </p>
                <p v-if="event.session_players"> 
                  {{ $t('pricingTable.session_players') }} {{ event.session_players }} 
                </p>
                <p v-if="event.networking_roulette"> 
                  {{ $t('pricingTable.networking_roulette') }} {{ event.networking_roulette }} 
                </p>
                <p v-if="event.iframe_player"> 
                  {{ $t('pricingTable.iframe_player') }} {{ event.iframe_player }} 
                </p>
                <p v-if="event.attendees"> 
                  {{ $t('pricingTable.downgrade_attendees') }} {{ event.attendees }} 
                </p>
                <p v-if="event.team_members"> 
                  {{ $t('pricingTable.team_members') }} {{ event.team_members }} 
                </p>
                <p v-if="event.event_survey"> 
                  {{ $t('pricingTable.event_survey') }} {{ event.event_survey }} 
                </p>
              </div>
              <p v-if="storageExceeded"> 
                {{ $t('pricingTable.storage_used') }} {{formatStorage(storageExceeded)+' GB'}} 
              </p>
            </div>
          </v-row>
        </v-card-text>
        <v-card-actions justify="center">
          <v-btn
            large
            outlined
            class="btn-outline-primary mb-2"
            @click="hasExceededData = false"
          >
            {{ $t('pricingTable.back') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Sure popup to proceed to downgrade -->
    <v-dialog
      v-if="canDowngrade"
      v-model="canDowngrade"
      max-width="900"
    >
      <v-card>
        <div class="ra__form-heading">
          <v-card-title class="ra__modal-title">
            <h3 class="ra__modal-title--heading"> {{ $t('pricingTable.sure') }}  </h3>
          </v-card-title>
          <v-btn icon @click="canDowngrade = false" aria-label="Close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text>
          <v-row class="mt-5">
            <v-col cols="12" md="12">
              <p>  {{ $t('pricingTable.proceedDowngrade') }} </p>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn
            outlined
            class="btn-primary mb-2 mt-2 align-center"
            @click="proceedToDowngrade(selectedPlan)"
          >
            {{ $t('pricingTable.proceed') }}
          </v-btn>
          <v-btn
            outlined
            class="btn-primary mb-2 mt-2 align-center"
            @click="canDowngrade = false"
          >
            {{ $t('pricingTable.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Display Pending cancel message popup -->
    <v-dialog
      v-if="displayCancelPopup"
      v-model="displayCancelPopup"
      max-width="900"
    >
      <v-card>
        <div class="ra__form-heading">
          <v-card-title class="ra__modal-title">
            <h3 class="ra__modal-title--heading"> {{ $t('pricingTable.unable_change_subscription') }}  </h3>
          </v-card-title>
          <v-btn icon @click="displayCancelPopup = false" aria-label="Close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text>
          <v-row class="mt-5">
            <v-col cols="12" md="12">
              <p>  {{ $t('pricingTable.change_subscription_body') }} </p>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
    <!--  dialog to make user get loggedin or register -->
    <v-dialog v-model="getLoggedIn" width="700">
      <v-card class="event-modal__wrap ra">
        <div class="ra__form-heading">
          <v-card-title class="ra__modal-title">
            <h3 class="ra__modal-title--heading mb-2">Login/Register</h3>
          </v-card-title>
        </div>
        <v-card-text class="invite__form mt-0 rounded-0">
          <v-row class="align-center">
            <v-col cols="12" lg="12">
              <div class="">
                <!-- New User -->
                <v-form
                  v-if="isNewUser"
                  ref="registerForm"
                  lazy-validation
                  @submit.prevent="register"
                  aria-autocomplete="off"
                  autocomplete="off"
                >
                  <v-alert
                    v-if="form.errors.get('error')"
                    outlined
                    transition="scale-transition"
                    type="error"
                  >
                    {{ form.errors.get("error") }}
                  </v-alert>
                  <v-row>
                    <v-col cols="12" md="4">
                      <label
                        class="ra__fields-label ra-fields--text-input__label"
                      >
                        {{ $t("first_name") }}
                      </label>
                    </v-col>
                    <v-col cols="12" md="8">
                      <v-text-field
                        ref="ref-registerForm-first-name"
                        v-model="form.first_name"
                        flat
                        solo
                        :rules="rules.firstNameRules"
                        :label="$t('first_name')"
                        required
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="4">
                      <label
                        class="ra__fields-label ra-fields--text-input__label"
                      >
                        {{ $t("last_name") }}
                      </label>
                    </v-col>
                    <v-col cols="12" md="8">
                      <v-text-field
                        ref="ref-registerForm-last-name"
                        v-model="form.last_name"
                        flat
                        solo
                        :rules="rules.lastNameRules"
                        :label="$t('last_name')"
                        :error-messages="form.errors.get('last_name')"
                        required
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="4">
                      <label
                        class="ra__fields-label ra-fields--text-input__label"
                      >
                        {{ $t("email") }}
                      </label>
                    </v-col>
                    <v-col cols="12" md="8">
                      <v-text-field
                        ref="ref-registerForm-email"
                        v-model="form.email"
                        flat
                        solo
                        aria-autocomplete="false"
                        :rules="rules.emailRules"
                        :label="$t('email')"
                        @change="checkEmailExist"
                        required
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="4">
                      <label
                        class="ra__fields-label ra-fields--text-input__label"
                      >
                        {{ $t("new_password") }}
                      </label>
                    </v-col>
                    <v-col cols="12" md="8">
                      <v-text-field
                        ref="ref-registerForm-password"
                        v-model="form.password"
                        type="password"
                        :error-messages="form.errors.get('password')"
                        :rules="rules.passwordRules"
                        flat
                        solo
                        :label="$t('new_password')"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="4">
                      <label
                        class="ra__fields-label ra-fields--text-input__label"
                      >
                        {{ $t("confirm_password") }}
                      </label>
                    </v-col>
                    <v-col cols="12" md="8">
                      <v-text-field
                        ref="ref-registerForm-confirm-password"
                        v-model="form.password_confirmation"
                        type="password"
                        :label="$t('confirm_password')"
                        :error-messages="form.errors.get('password_confirmation')"
                        :rules="rules.confirmPasswordRules.concat(passwordConfirmationRule)"
                        flat
                        solo
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-btn
                        ref="ref-registerForm-submit-btn"
                        x-large
                        type="submit"
                        block
                        :loading="form.busy"
                        class="btn-primary"
                      >
                        Continue
                        <v-icon light> mid-key </v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
                <!-- Existing User -->
                <v-form
                  v-else
                  ref="login"
                  lazy-validation
                  @submit.prevent="login"
                >
                  <v-alert
                    v-if="form.errors.get('error')"
                    outlined
                    transition="scale-transition"
                    type="error"
                  >
                    {{ form.errors.get("error") }}
                  </v-alert>
                  <v-row>
                    <v-col cols="12" md="4">
                      <label
                        class="ra__fields-label ra-fields--text-input__label"
                      >
                        {{ $t("email") }}
                      </label>
                    </v-col>
                    <v-col cols="12" md="8">
                      <v-text-field
                        ref="ref-registerForm-email"
                        v-model="form.email"
                        flat
                        solo
                        label="Enter Email Address"
                        @keyup="checkEmailExist"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="4">
                      <label
                        class="ra__fields-label ra-fields--text-input__label"
                      >
                        {{ $t("password") }}
                      </label>
                    </v-col>
                    <v-col cols="12" md="8">
                      <v-text-field
                        ref="ref-registerForm-password"
                        v-model="form.password"
                        type="password"
                        :error-messages="form.errors.get('password')"
                        flat
                        solo
                        :label="$t('password')"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="12">
                      <v-btn
                        ref="ref-registerForm-submit-btn"
                        x-large
                        type="submit"
                        block
                        :loading="form.busy"
                        class="btn-primary"
                      >
                        <span class="white--text"> Buy Now </span>
                        <v-icon light> mid-key </v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-col>
</template>
<script>
import isEmpty from 'lodash/isEmpty'
import RaLoading from '~/components/forms/RaLoading'
import planAttributes from '~/collections/planAttributes.json'
import RaCurrencySelection from '~/components/billing/RaCurrencySelection'
import RaModalCloseButton from '~/components/general/RaModalCloseButton'
import axios from "axios"
import Form from "vform"

import {
  getAllSalesPlans,
  formattedPrice,
  getDefaultCurrencyCode,
  getSettingForPlan,
  getSubscriptionDetails
} from '~/plugins/billing/billing'
import { mapGetters } from 'vuex'
export default {
  components: { RaCurrencySelection, RaLoading, RaModalCloseButton },
  layout (context) {
    return context.authenticated ? 'basic' : 'default'
  },
  data () {
    return {
      linkName: 'eventSubscribe',
      subscribedPlan: null,
      currency: {}, // Will initialise to cashier default
      planAttributes: planAttributes,
      isLoading: true,
      isLoadingCurrencies: true,
      isBuildingTable: true,
      defaultCurrencyCode: '',
      plans: [],
      noSectionHeader: ['planPrices'],
      tableColumns: [
        {
          title: ''
        },
        {
          planName: 'replay',
          title: 'Replay',
          link: ''
        },
        {
          planName: 'small',
          title: 'Small',
          link: ''
        },
        {
          planName: 'plus',
          title: 'Plus',
          link: ''
        },
        {
          planName: 'pro',
          title: 'Event Pro',
          link: ''
        },
        {
          planName: 'business',
          title: 'Business',
          link: ''
        },
        {
          planName: 'platinum',
          title: 'Platinum',
          link: ''
        }
      ],
      tableRows: [],
      plansForMobileView: [],
      displayAttributes: {
        supportHelpContact: {
          helpdesk: 'Yes - Helpdesk on Event + Electronic 24 hour SLA',
          live: 'Yes - Project Managed + Dedicated Event Live Staff',
          sla: 'Yes - 24 Hour SLA Support (Electronic)',
          '24hr': 'Yes - 24 Hour Support (Electronic)'
        },
        streamingQuality: {
          q720: '720p',
          q1080: '1080p'
        },
        analytics: {
          basic: 'Basic',
          standard: 'Standard',
          full: 'Full'
        },
        costingAnalytics: {
          admin: 'Admin Only'
        },
        eventPlanning: {
          admin: 'Admin Only'
        }
      },
      hasExceededData :  false,
      storageExceeded : null,
      eventsData : null,
      canDowngrade : false,
      selectedPlan : null,
      displayCancelPopup: false,
      getLoggedIn: false,
      isNewUser: true,
      form: new Form({
        email: "",
        password: "",
      }),
      formError: "",
      rules: {
        firstNameRules: [(v) => !!v || "First Name is required"],
        lastNameRules: [(v) => !!v || "Last Name is required"],
        emailRules: [
          (v) => !!v || "E-mail is required",
          (v) =>
            /^([A-Za-z0-9_\-.+])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,})$/.test(v) ||
            "E-mail must be valid",
        ],
        passwordRules: [
          (v) => !!v || "Password is required",
          (v) =>
            (v && v.length >= 8) || "Password must be more than 8 characters",
          (v) => /(?=.*[a-z])/.test(v) || "Must have one lower character",
          (v) => /(?=.*[A-Z])/.test(v) || "Must have one uppercase character",
          (v) => /(?=.*\d)/.test(v) || "Must have one number",
          (v) => /([!@$%])/.test(v) || "Must have one special character [!@#$%]",
        ],
        confirmPasswordRules: [(v) => !!v || "Confirm Password is required"],
      }
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/user',
      authenticated: 'auth/check'
    }),
    columnCount () {
      return this.tableColumns.length
    },
    canDisplayTable () {
      return !this.isLoading && !this.isLoadingCurrencies && !this.isBuildingTable
    },
    tableColumnsPlans () {
      // Get all table columns which are plans (i.e. not the title col)
      // eslint-disable-next-line no-prototype-builtins
      return this.tableColumns.filter(col => col.hasOwnProperty('planName'))
    },
    userPlanColumnIndex () {
      // Gets the plan column index for the user's currently subscribed plan
      return !isEmpty(this.subscribedPlan) && this.subscribedPlan.isActive ? this.getIndexForPlan(this.subscribedPlan.planName) : undefined
    },
    hasStripeId () {
      return this.authenticated && !isEmpty(this.user.stripe_id)
    },
    userCurrencyId () {
      return this.authenticated ? this.user.currency_id : 0
    },
    colsMd () {
      return this.authenticated ? 9 : 12
    },
    colsLg () {
      return this.authenticated ? 10 : 12
    },
    periodicals () {
      return this.planAttributes.filter(plan => plan.sectionName === 'planPrices' )[0]
    },
    passwordConfirmationRule() {
      return () =>
        this.form.password === this.form.password_confirmation ||
        "The password confirmation does not match.";
    }
  },
  async mounted () {
    this.isLoading = true
    this.defaultCurrencyCode = getDefaultCurrencyCode()
    this.currency.iso_code = getDefaultCurrencyCode()
    // Get all salesplans and prices
    await this.getSalesPlansAndPrices()
    if(this.user){
      this.subscribedPlan = getSubscriptionDetails(this.user)
    }
    this.isLoading = false
    this.buildTableRows()
    if (!this.authenticated) {
      this.linkName = 'purchaseTicket'
    }
  },
  updated () {
    // This code for set table grouping accordian

    const elements = document.getElementsByClassName('btn__click')
    elements.forEach((element) => {
      element.addEventListener('click', function () {
        let e = this.parentNode
        e.classList.toggle('active')
        e = e.nextSibling
        while (e && !e.classList.contains('parent')) {
          if (e.style.display !== 'none') {
            e.style.display = 'none'
          } else {
            e.style = ''
          }
          e = e.nextSibling
        }
      })
    })

    // This code for set rainbow color in table

    document.querySelectorAll('.parent').forEach(function (i, index) {
      const newClass = 'parent-title-' + index
      i.classList.add(newClass)
    })
  },
  methods: {
    buttonDetails (planName) {
      if (!this.userPlanColumnIndex) {
        return {
          isDisabled: false,
          text: 'Buy Now',
          subscriptionText: 'create'
        }
      }
      const index = this.getIndexForPlan(planName)
      if (index < this.userPlanColumnIndex) {
        return {
          isDisabled: false,
          text: 'downgrade',
          subscriptionText: 'downgrade'
        }
      }
      if (index === this.userPlanColumnIndex) {
        return {
          isDisabled: true,
          text: 'subscribed',
          isSubscribed: true
        }
      }
      return {
        isDisabled: false,
        text: 'upgrade',
        subscriptionText: 'upgrade'
      }
    },
    buttonLink (column) {
      if (isEmpty(this.user)) {
        this.getLoggedIn = true;
      } else {
        let hasCanceled
        axios
          .get('/user/check-subscription-status')
          .then((response) => {
            hasCanceled = response.data.hasCanceled
            if (hasCanceled == true) {
              this.displayCancelPopup = true
            } else if(this.buttonDetails(column.planName).text == 'downgrade') {
              return this.checkDowngrade(column)
            } else {
              return this.$router.push({ name: this.linkName, query: { plan: column.planName,currency: this.currency.iso_code,subscriptionType:this.buttonDetails(column.planName).subscriptionText }})
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    },
    getIndexForPlan (thisPlanName) {
      //return this.tableColumnsPlans.findIndex(({ planName }) => thisPlanName === planName)
      let val =  this.tableColumnsPlans.findIndex(({ planName }) => thisPlanName === planName)
      return parseInt(val) + 1
    },
    onSelectCurrency (currency) {
      this.currency = currency
      this.buildTableRows()
    },
    openHelpInfo (value) {
      this.$refs[value][0].style.display = 'block'
    },
    closeHelpInfo (value) {
      this.$refs[value][0].style.display = 'none'
    },
    async getSalesPlansAndPrices () {
      const resp = await getAllSalesPlans()
      if (resp.notification) {
        this.raiseNotification(resp.notification)
      } else {
        this.plans = resp.plans
      }
    },
    raiseNotification (notification) {
      this.$store.dispatch('notifications/addNotification', notification, { root: true })
    },
    buildTableRows () {
      // Build Matrix for Mobile view also
      this.buildMobileViewMatrix()
      // Build all table rows
      this.isBuildingTable = true
      const matrixRows = []
      this.planAttributes.forEach(({ sectionName, hasSection, attributes }) => {
        if (hasSection) {
          // Create section header
          matrixRows.push({
            isSectionHead: true,
            name: this.$t(`salesPlans.${sectionName}`),
            id: sectionName,
            class: 'section-header',
            key: sectionName
          })
        }
        // For each attribute we want to display as a row
        attributes.forEach(({ setting, type }) => {
          // Set the row title
          let row = {
            name: this.$t(`salesPlans.${setting}`),
            class: sectionName,
            isSectionHead: false,
            key: setting
          }
          // For each plan in our table
          this.tableColumnsPlans.forEach(({ planName }) => {
            // Add attribute value for plan to the row
            row = { ...row, ...this.getAttribute({ setting, type, planName }) }
          })
          matrixRows.push(row)
        })
      })
      this.tableRows = matrixRows
      this.isBuildingTable = false
    },
    getAttribute ({ setting, type, planName }) {
      // Each plan attribute has a processing type - get the value for the attribute
      switch (type) {
        case 'planPrice': {
          return { [planName]: this.getPrice({ setting: setting, planName: planName }) }
        }
        case 'numeric': {
          const settingValue = getSettingForPlan({ setting: setting, planName: planName })
          return { [planName]: settingValue === -1 ? 'Unlimited' : settingValue }
        }
        case 'boolean':
        {
          return { [planName]: getSettingForPlan({ setting: setting, planName: planName }) }
        }
        case 'string':
        {
          return { [planName]: this.lookUpString({ setting, planName }) }
        }
        case 'percent':
        {
          const percent = (getSettingForPlan({ setting: setting, planName: planName }) * 100).toFixed(1)
          return { [planName]: `${percent}%` }
        }
        case 'noNumeric': {
          const settingValue = getSettingForPlan({ setting: setting, planName: planName })
          return { [planName]: settingValue === 0 || settingValue === -1 ? 'no' : settingValue }
        }
        default:
          console.log(`Unexpected plan attribute type ${type}`)
          return { [planName]: 'error' }
      }
    },
    lookUpString ({ setting, planName }) {
      // Convert value for setting into display value for setting
      const settingValue = getSettingForPlan({ setting: setting, planName: planName })
      // eslint-disable-next-line no-prototype-builtins
      return this.displayAttributes.hasOwnProperty(setting) && this.displayAttributes[setting].hasOwnProperty(settingValue)
        ? this.displayAttributes[setting][settingValue]
        : settingValue
    },
    getPrice ({ setting, planName }) {
      switch (setting) {
        case 'priceMonthly': {
          return this.getSalesPrice({ interval: 'month', planName: planName, isEventPlan: true })
        }
        case 'priceAnnually': {
          return this.getSalesPrice({ interval: 'year', planName: planName, isEventPlan: true })
        }
        case 'priceAdditionalSingleUseSeats': {
          return this.getSalesPrice({ planName: 'single_use_seats_100', isEventPlan: false })
        }
        case 'priceAdditionalRecurringUseSeats': {
          return this.getSalesPrice({ planName: 'recurring_seats_100', isEventPlan: false })
        }
        case 'storageAdditionalGbPack': {
          return this.getSalesPrice({ planName: 'storage_20', isEventPlan: false })
        }
        case 'additionalTeamMembersMonthly': {
          return this.getSalesPrice({ planName: 'team_member', isEventPlan: false })
        }
        default:
          console.log(`Unexpected plan attribute setting ${setting}`)
      }
    },
    getSalesPrice (params) {
      // Return formatted price for plan
      const plan = this.plans.find(({ type }) => type === params.planName)
      if (!plan) { return 'TBD' }

      const priceObject = plan.prices.find((price) => {
        // eslint-disable-next-line no-prototype-builtins
        return price.currency_info.id === this.currency.id && (params.hasOwnProperty('interval') ? price.interval === params.interval : true)
      })
      // eslint-disable-next-line no-prototype-builtins
      if (!priceObject || !priceObject.hasOwnProperty('price')) { return 'N/A' }
      return formattedPrice({ value: priceObject.price, isoCode: this.currency.iso_code, roundToInteger: params.isEventPlan })
    },
    helpInfoColumn (planName) {
      const colInfo = window.config.helpinfo['pricing-help-popup'].plansColumns.find((e) => e.key == planName)
      if (colInfo) {
        return colInfo.value
      }
    },
    helpInfoRow (planName) {
      const rowInfo = window.config.helpinfo['pricing-help-popup'].plansRows.find((e) => e.key == planName)
      if (rowInfo) {
        return rowInfo.value
      }
    },
    showHelpIcon (title) {
      return !!title
    },
    getHelpColName (planName) {
      const colInfo = window.config.helpinfo['pricing-help-popup'].plansColumns.find((e) => e.key == planName)
      if (colInfo) {
        return colInfo.title
      }
    },
    getHelpRowName (planName) {
      const rowInfo = window.config.helpinfo['pricing-help-popup'].plansRows.find((e) => e.key == planName)
      if (rowInfo) {
        return rowInfo.title
      }
    },
    buildMobileViewMatrix () {
      let mobileViewPlans = []
      // get the Monthly and yearly prices for plan
      const monthlyPlanSetting = this.periodicals ? this.periodicals.attributes.find(plan =>  plan.setting === 'priceMonthly') : ''
      const yearlyPlanSetting = this.periodicals ? this.periodicals.attributes.find(plan =>  plan.setting === 'priceAnnually') : ''

      this.tableColumnsPlans.forEach((plan) => {
        let planName = plan.planName
        let monthlyPrice = this.getPrice({ setting: monthlyPlanSetting.setting, planName: planName })
        let yearlyPrice  = this.getPrice({ setting: yearlyPlanSetting.setting, planName: planName })
        plan.monthly_price = monthlyPrice
        plan.yearly_price = yearlyPrice
        plan.sections = []

        this.planAttributes.filter( section => section.hasSection ).forEach(({ sectionName, hasSection, attributes }) => {
          let section = {
            isSectionHead: true,
            name: this.$t(`salesPlans.${sectionName}`),
            id: sectionName,
            class: 'section-header',
            key: sectionName,
            attributes: []
          }

          attributes.forEach(({ setting, type }) => {
            // Set the row title
            let row = {
              name: this.$t(`salesPlans.${setting}`),
              class: sectionName,
              isSectionHead: false,
              key: setting
            }
            section.attributes.push({ ...row, ...this.getAttribute({ setting, type,  planName}) })
          })
          plan.sections.push(section)
        })
        mobileViewPlans.push(plan)
      })
      this.plansForMobileView = mobileViewPlans
    },
    checkDowngrade(item){
      axios
        .post('/user/can-downgrade',{plan:item.planName})
        .then((response) => {
          this.hasExceededData = !isEmpty(response.data.eventsData) || response.data.storageExceeded != null ? true : false
          this.canDowngrade = isEmpty(response.data.eventsData) && response.data.storageExceeded == null ? true : false
          this.eventsData   = response.data.eventsData
          this.selectedPlan = item
          this.storageExceeded = response.data.storageExceeded
        })
        .catch((error) => {
          console.log(error);
        });
    },
    proceedToDowngrade(column){
      this.selectedPlan = null 
      return this.$router.push({ name: this.linkName, query: { plan: column.planName,currency: this.currency.iso_code,subscriptionType:this.buttonDetails(column.planName).subscriptionText }})
    },
    formatStorage(item){
      if (item != null) {
        return Number(item).toFixed(3);
      }
    },
    checkEmailExist() {
      axios
        .get("check-email/" + this.form.email)
        .then((response) => {
          if (response.data === "notExist") {
            this.isNewUser = true;
          } else {
            this.isNewUser = false;
            this.$store.dispatch("snackbar/showSnack", {
              text:
                "This email is already linked to an account. Please login to complete your purchase",
              color: "warning",
              timeout: 2000,
            });
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async register() {
      if (this.isNewUser && this.$refs.registerForm.validate()) {
        try {
          // Change Password the user.
          const { data } = await this.form.post("/purchase-ticket");
          // Must verify email first.
          if (data.status) {
            this.mustVerifyEmail = true;
          } else {
            // Log in the user.
            const { data } = await this.form.post("/login");
            // Save the token.
            this.$store.dispatch("auth/saveToken", {
              token: data.access_token,
            });
            this.afterLoginCalls();
          }
        } catch (e) {
          console.log(e);
        }
      }
    },
    async login() {
      if (!this.isNewUser && this.$refs.login.validate()) {
        try {
          const response = await this.form.post("/login");
          this.$store.dispatch("auth/saveToken", {
            token: response.data.access_token,
            remember: this.remember,
          });
          this.afterLoginCalls();
        } catch (e) {
          this.showErrorMessage = true;
          this.errorText = this.$t("login_error_text");
          console.log(e);
        }
      }
    },
    async afterLoginCalls() {
      // Fetch the user.
      await this.$store.dispatch("auth/fetchUser");
      this.getLoggedIn = false;
      location.reload()
    },
  }
}

</script>